<script setup lang="ts">
import { useTrip } from '@/stores/trip';
import SegmentButton from '@/components/ui/SegmentButton.vue';
import Chip from '@/components/ui/Chip.vue';

/**
 * Contrôles du voyage : date/heure de départ, mode de transport,
 * slider de temps maximum affiché, filtre "sans correspondance".
 *
 * Tout le state vit dans le store ; ce composant n'est qu'un binding réactif.
 */
const trip = useTrip();

const MODES = [
  { key: 'best', label: 'Rapide', icon: 'ti-bolt', title: '' },
  { key: 'train', label: 'Train', icon: 'ti-train', title: '' },
  { key: 'bus', label: 'Bus', icon: 'ti-bus', title: '' },
  {
    key: 'night',
    label: 'Nuit',
    icon: 'ti-moon',
    title: 'Privilégie les trains de nuit pour économiser une nuit d\'hôtel',
  },
] as const;

function clearDate() {
  trip.setStartDate(null);
}
</script>

<template>
  <div class="space-y-5">
    <!-- Date et heure -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-medium text-text">Quand partez-vous ?</span>
        <button
          v-if="trip.state.tripStartDate"
          type="button"
          class="text-text-faint hover:text-text text-sm"
          title="Effacer la date"
          @click="clearDate"
        >
          <i class="ti ti-x" aria-hidden="true"></i>
        </button>
      </div>
      <div class="flex gap-2">
        <input
          type="date"
          :value="trip.state.tripStartDate || ''"
          class="flex-1 px-3 py-2 bg-surface border border-DEFAULT rounded-md text-sm outline-none focus:border-accent transition-colors duration-160"
          @change="trip.setStartDate(($event.target as HTMLInputElement).value || null)"
        />
        <input
          type="time"
          :value="trip.state.tripStartTime"
          class="w-28 px-3 py-2 bg-surface border border-DEFAULT rounded-md text-sm outline-none focus:border-accent transition-colors duration-160"
          @change="trip.setStartTime(($event.target as HTMLInputElement).value || '09:00')"
        />
      </div>
    </div>

    <!-- Mode de transport -->
    <div>
      <div class="text-sm font-medium text-text mb-2">Comment voyager ?</div>
      <div class="flex gap-1 p-1 bg-surface-muted rounded-md">
        <SegmentButton
          v-for="m in MODES"
          :key="m.key"
          :active="trip.state.routeMode === m.key"
          :icon="m.icon"
          :title="m.title"
          @click="trip.setMode(m.key)"
        >
          {{ m.label }}
        </SegmentButton>
      </div>
    </div>

    <!-- Slider temps max -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-medium text-text">Distance affichée (temps max)</span>
        <span class="text-sm font-mono text-accent">{{ trip.state.maxHours }}h</span>
      </div>
      <input
        type="range"
        min="1"
        max="24"
        step="1"
        :value="trip.state.maxHours"
        class="w-full accent-accent"
        @input="trip.setMaxHours(parseInt(($event.target as HTMLInputElement).value, 10))"
      />
      <div class="flex justify-between text-xs text-text-faint mt-1">
        <span>1h</span>
        <span>6h</span>
        <span>12h</span>
        <span>24h</span>
      </div>
    </div>

    <!-- Filtre direct -->
    <div>
      <div class="text-sm font-medium text-text mb-2">Affiner la carte</div>
      <Chip
        :active="trip.state.routeDirectOnly"
        icon="ti-arrow-right"
        title="N'afficher que les villes accessibles sans correspondance depuis la dernière étape"
        @click="trip.setDirectOnly(!trip.state.routeDirectOnly)"
      >
        Trajets sans correspondance
      </Chip>
    </div>
  </div>
</template>
